import { useEffect, useState } from 'react'
import { cx } from './cx.ts'
import { motionReduced } from './motion.tsx'

export interface CompareDatum {
  key: string
  /** The words under the axis for this bar ("5"), or nothing to leave it unlabelled. */
  axis?: string
  /** The tooltip's heading: "Sun 9 Kartik". */
  title: string
  /** Null for a slot still to come (later this month): nothing is drawn. */
  value: number | null
  /** The same slot in the period it is compared with (last month, last week). */
  compare?: number | null
  /** A short extra for the tooltip: "14 bills". */
  note?: string
}

/** A round top for the axis: 1, 2, 2.5 or 5 times a power of ten, split into four steps. */
export function niceSteps(max: number, steps = 4): number[] {
  if (max <= 0) return [0, 1]
  const raw = max / steps
  const power = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * power >= raw) ?? 10) * power
  return Array.from({ length: steps + 1 }, (_, i) => i * step)
}

/**
 * Bars against the same slots of another period (docs/plans/dashboard-refresh.md): one slot per
 * day of the month or hour of the day, so the frame is whole from the first day; slots still
 * to come stay empty. The highlighted slot (today) is the full primary and named under the
 * axis; the others are a lighter step of it. The other period is a short gray tick across
 * each slot. Hover, a tap or the arrow keys show a slot's value, its note and the difference.
 */
export function CompareBars({
  data,
  format,
  formatShort = format,
  formatTick,
  summary,
  seriesLabel,
  compareLabel,
  compareWords,
  highlight,
  highlightLabel = 'Today',
  height = 180,
  grow = false,
  best = true,
  className,
}: {
  data: readonly CompareDatum[]
  format: (value: number) => string
  /** Money on the chart itself (the best slot, today): "6,120". Defaults to `format`. */
  formatShort?: (value: number) => string
  /** The axis' round numbers, short: "2K". */
  formatTick: (value: number) => string
  summary: string
  /** The legend's words for the bars and the ticks. */
  seriesLabel: string
  compareLabel?: string
  /** After a compared value in the tooltip: "same day in Asoj". */
  compareWords?: string
  highlight?: number
  highlightLabel?: string
  height?: number
  grow?: boolean
  /** Name the best slot once, above its bar (wide screens). */
  best?: boolean
  className?: string
}) {
  const [grown, setGrown] = useState(() => !grow || motionReduced())
  useEffect(() => {
    if (grown) return
    const frame = requestAnimationFrame(() => setGrown(true))
    return () => cancelAnimationFrame(frame)
  }, [grown])
  const [active, setActive] = useState<number | null>(null)
  const ticks = niceSteps(Math.max(0, ...data.map((d) => Math.max(d.value ?? 0, d.compare ?? 0))))
  const top = ticks[ticks.length - 1] ?? 1
  const share = (v: number) => Math.max(0, Math.min(1, v / top))
  const bestIndex = best
    ? data.reduce((b, d, i) => ((d.value ?? 0) > (data[b]?.value ?? 0) ? i : b), 0)
    : -1
  const n = data.length
  const pick = (clientX: number, el: HTMLElement) => {
    const box = el.getBoundingClientRect()
    const i = Math.floor(((clientX - box.left) / box.width) * n)
    const idx = Math.max(0, Math.min(n - 1, i))
    setActive(data[idx]?.value === null ? null : idx)
  }
  const shown = active !== null ? data[active] : undefined
  const at = (i: number) => `${((i + 0.5) / n) * 100}%`
  const anchor = (i: number) => (i < n * 0.35 ? 'left' : i > n * 0.65 ? 'right' : 'center')
  const place = (i: number) => {
    const side = anchor(i)
    return side === 'left'
      ? { left: `calc(${at(i)} - 12px)` }
      : side === 'right'
        ? { right: `calc(${100 - ((i + 0.5) / n) * 100}% - 12px)` }
        : { left: at(i), transform: 'translateX(-50%)' }
  }
  const lit = highlight !== undefined ? data[highlight] : undefined
  return (
    <div className={cx('w-full', className)}>
      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted" aria-hidden>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-t-sm bg-primary/70" />
          {seriesLabel}
        </span>
        {compareLabel && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-3.5 rounded-full bg-neutral" />
            {compareLabel}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <div
          className="relative w-8 shrink-0 text-right text-[11px] text-subtle"
          style={{ height }}
          aria-hidden
        >
          {ticks.map((t) => (
            <span
              key={t}
              className="tabular absolute right-0"
              style={{ bottom: `calc(${share(t) * 100}% - 0.5em)` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1">
          <div
            role="img"
            aria-label={summary}
            tabIndex={0}
            className="relative touch-pan-y rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            style={{ height }}
            onPointerMove={(e) => {
              if (e.pointerType === 'mouse') pick(e.clientX, e.currentTarget)
            }}
            onPointerDown={(e) => pick(e.clientX, e.currentTarget)}
            onPointerLeave={(e) => {
              if (e.pointerType === 'mouse') setActive(null)
            }}
            onFocus={() => setActive(highlight ?? null)}
            onBlur={() => setActive(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') return setActive(null)
              if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
              e.preventDefault()
              const last = data.reduce((l, d, i) => (d.value !== null ? i : l), 0)
              setActive((a) =>
                Math.max(
                  0,
                  Math.min(last, (a ?? highlight ?? 0) + (e.key === 'ArrowLeft' ? -1 : 1)),
                ),
              )
            }}
          >
            {ticks.map((t) => (
              <span
                key={t}
                aria-hidden
                className={cx('absolute inset-x-0 h-px', t === 0 ? 'bg-border' : 'bg-soft')}
                style={{ bottom: `${share(t) * 100}%` }}
              />
            ))}
            <div className="absolute inset-0 flex" aria-hidden>
              {data.map((d, i) => (
                <div
                  key={d.key}
                  className={cx('relative h-full min-w-0 flex-1', active === i && 'bg-primary/5')}
                >
                  {d.value !== null && d.value > 0 && (
                    <span
                      className={cx(
                        'absolute bottom-0 left-1/2 h-full origin-bottom rounded-t-sm transition-transform duration-300 ease-out',
                        i === highlight || i === active ? 'bg-primary' : 'bg-primary/70',
                      )}
                      style={{
                        width: 'min(14px, 56%)',
                        transform: `translateX(-50%) scaleY(${grown ? share(d.value) : 0})`,
                      }}
                    />
                  )}
                  {d.value === 0 && (
                    <span className="absolute bottom-0 left-1/2 h-0.5 w-1 -translate-x-1/2 rounded-full bg-subtle/60" />
                  )}
                  {d.value !== null && d.value < 0 && (
                    <span className="absolute bottom-0 left-1/2 h-[3px] w-[min(14px,56%)] -translate-x-1/2 rounded-t-sm bg-danger" />
                  )}
                  {d.value !== null && !!d.compare && (
                    <span
                      className="absolute left-1/2 h-0.5 -translate-x-1/2 rounded-full bg-neutral ring-2 ring-surface"
                      style={{
                        bottom: `${share(d.compare) * 100}%`,
                        width: 'calc(min(14px, 56%) + 6px)',
                      }}
                    />
                  )}
                  {i === bestIndex && i !== highlight && (d.value ?? 0) > 0 && (
                    <span
                      className="absolute left-1/2 -translate-x-1/2 text-[11px] whitespace-nowrap text-muted max-sm:hidden"
                      style={{ bottom: `calc(${share(d.value ?? 0) * 100}% + 4px)` }}
                    >
                      Best {formatShort(d.value ?? 0)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {shown && active !== null && shown.value !== null && (
            <div
              aria-live="polite"
              className="pointer-events-none absolute bottom-full z-10 mb-1 min-w-44 rounded-xl border border-border bg-raised px-3 py-2 text-[13px] shadow-card-hover"
              style={place(active)}
            >
              <p className="text-muted">{shown.title}</p>
              <p className="mt-0.5 flex items-center gap-2">
                <span className="size-2.5 rounded-t-sm bg-primary/70" aria-hidden />
                <span className="tabular font-bold whitespace-nowrap text-ink">
                  {format(shown.value)}
                </span>
                {shown.note && <span className="text-muted">{shown.note}</span>}
              </p>
              {shown.compare != null && (
                <>
                  <p className="flex items-center gap-2">
                    <span className="h-0.5 w-3 rounded-full bg-neutral" aria-hidden />
                    <span className="tabular whitespace-nowrap">{format(shown.compare)}</span>
                    {compareWords && <span className="text-muted">{compareWords}</span>}
                  </p>
                  {shown.value !== shown.compare && (
                    <p
                      className={cx(
                        'mt-1 font-semibold',
                        shown.value > shown.compare ? 'text-success' : 'text-muted',
                      )}
                    >
                      {shown.value > shown.compare ? '↑' : '↓'}{' '}
                      {format(Math.abs(shown.value - shown.compare))}{' '}
                      {shown.value > shown.compare ? 'more' : 'less'}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
          <div className="relative mt-1.5 h-4 text-[11px] text-subtle" aria-hidden>
            {data.map((d, i) =>
              d.axis && i !== highlight ? (
                <span key={d.key} className="absolute -translate-x-1/2" style={{ left: at(i) }}>
                  {d.axis}
                </span>
              ) : null,
            )}
          </div>
          {lit && highlight !== undefined && (
            <p
              className="absolute -bottom-5 text-[11.5px] font-bold whitespace-nowrap text-primary"
              style={place(highlight)}
              aria-hidden
            >
              {highlightLabel}
              {lit.value ? ` · ${formatShort(lit.value)}` : ''}
            </p>
          )}
        </div>
      </div>
      <div className="h-5" aria-hidden />
    </div>
  )
}

export interface StackPart {
  key: string
  label: string
  value: number
  tone: 'neutral' | 'success' | 'warning' | 'late' | 'danger'
}

const STACK_TONES: Record<StackPart['tone'], string> = {
  neutral: 'bg-neutral',
  success: 'bg-success',
  warning: 'bg-warning',
  late: 'bg-danger/65',
  danger: 'bg-danger',
}

/** Whole percents that add up to 100 (largest remainders), so a legend never says 101%. */
export function stackShares(values: readonly number[]): number[] {
  const total = values.reduce((a, v) => a + Math.max(0, v), 0)
  if (total <= 0) return values.map(() => 0)
  const exact = values.map((v) => (Math.max(0, v) * 100) / total)
  const shares = exact.map(Math.floor)
  const order = exact.map((e, i) => ({ i, left: e - Math.floor(e) }))
  order.sort((a, b) => b.left - a.left)
  let spare = 100 - shares.reduce((a, s) => a + s, 0)
  for (const { i } of order) {
    if (spare-- <= 0) break
    shares[i]! += 1
  }
  return shares
}

/**
 * One bar split into named parts (money by age, quotations by outcome), with a legend that says
 * each part's words, amount and share, so colour is never alone.
 */
export function StackBar({
  parts,
  format,
  summary,
  className,
}: {
  parts: readonly StackPart[]
  format: (value: number) => string
  summary: string
  className?: string
}) {
  const shares = stackShares(parts.map((p) => p.value))
  return (
    <div className={cx('w-full', className)}>
      <div
        role="img"
        aria-label={summary}
        className="flex h-4 gap-0.5 overflow-hidden rounded-lg bg-soft"
      >
        {parts.map(
          (p) =>
            p.value > 0 && (
              <span
                key={p.key}
                className={cx('min-w-1', STACK_TONES[p.tone])}
                style={{ flexGrow: p.value, flexBasis: 0 }}
              />
            ),
        )}
      </div>
      <ul
        className={cx(
          'mt-3 grid grid-cols-2 gap-2',
          parts.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-4',
        )}
      >
        {parts.map((p, i) => (
          <li key={p.key} className="min-w-0 rounded-xl border border-border px-3 py-2.5">
            <span className="flex items-center gap-1.5 text-xs text-muted">
              <span
                className={cx('size-2.5 shrink-0 rounded-full', STACK_TONES[p.tone])}
                aria-hidden
              />
              {p.label}
            </span>
            <span className="tabular mt-0.5 block font-bold break-words text-ink">
              {format(p.value)}
            </span>
            <span className="text-xs text-subtle">{shares[i]}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
